<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Bar } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
    import type { TooltipItem } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { formatNumber } from '@shared/utils/formatNumber';
    import type { IssueDailyStatistics } from '@views/Issues/interfaces';
    import { chartColor } from '@shared/modules/chart-colors';
    import BSToggleButtons from '@shared/components/BSToggleButtons.vue';
    import { formatCompact, rootFontSize } from '../insights-theme';

    ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);
    applyChartDefaults();

    const props = defineProps<{
        eventsOverTime: IssueDailyStatistics[];
    }>();

    const METRICS = [
        { key: 'sent', label: 'Sent', color: 'eventSent' },
        { key: 'opened', label: 'Opened', color: 'eventOpened' },
        { key: 'clicked', label: 'Clicked', color: 'eventClicked' },
        { key: 'bounced', label: 'Bounced', color: 'eventBounced' },
        { key: 'unsubscribed', label: 'Unsubscribed', color: 'eventUnsubscribed' },
    ] as const;

    type MetricKey = (typeof METRICS)[number]['key'];

    const METRIC_OPTIONS = METRICS.map((m) => ({ name: m.label, value: m.key }));

    const selected = ref<MetricKey>('sent');
    const metric = computed(() => METRICS.find((m) => m.key === selected.value) ?? METRICS[0]);
    const series = computed(() => props.eventsOverTime.find((s) => s.TypeName === selected.value)?.Values ?? []);
    const total = computed(() => series.value.reduce((acc, v) => acc + v.Count, 0));

    function formatDay(date: string, long = false): string {
        return new Date(`${date}T12:00:00`).toLocaleDateString(
            navigator.language,
            long ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short' }
        );
    }

    const chartData = computed(() => ({
        labels: series.value.map((v) => formatDay(v.Date)),
        datasets: [
            {
                label: metric.value.label,
                data: series.value.map((v) => v.Count),
                backgroundColor: chartColor(metric.value.color),
                borderSkipped: 'bottom' as const,
                categoryPercentage: 0.8,
                barPercentage: 0.9,
            },
        ],
    }));

    const chartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'bar'>[]) => formatDay(series.value[items[0].dataIndex].Date, true),
                    label: (item: TooltipItem<'bar'>) => `${metric.value.label}: ${formatNumber(item.parsed.y)}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: { font: { size: rootFontSize('--font-size-small') }, maxRotation: 0, autoSkipPadding: 12 },
            },
            y: {
                beginAtZero: true,
                border: { display: false },
                grid: { color: chartColor('grid') },
                ticks: {
                    font: { size: rootFontSize('--font-size-small') },
                    maxTicksLimit: 5,
                    callback: (value: string | number) => formatCompact(Number(value)),
                },
            },
        },
    }));
</script>

<template>
    <div>
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-xs m-sm-b">
            <BSToggleButtons
                :options="METRIC_OPTIONS"
                :value="selected"
                button-class="btn-sm"
                aria-label="Metric"
                @update:value="(key: MetricKey) => (selected = key)" />
            <span>
                <strong>{{ formatNumber(total) }}</strong>
                {{ metric.label.toLowerCase() }} in {{ series.length }} days
            </span>
        </div>
        <div class="insights-chart p-relative">
            <Bar :data="chartData" :options="chartOptions" :aria-label="`${metric.label} per day over ${series.length} days`" role="img" />
        </div>
    </div>
</template>

